import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceDto, PairingCodeMinted } from '@gc/shared';

import { api } from '@/lib/api';

/**
 * Mint and display a pairing code for one PC (WAVE2-DESIGN §3).
 *
 * The dialog mints on open — the action IS "give me a code", there is no
 * separate confirm step — and shows the plaintext exactly once: the server
 * stores only a hash, so closing this dialog loses the code forever and the
 * recovery is simply minting another. Minting again also expires whatever was
 * minted before, so an over-the-shoulder photo of an old code dies the moment
 * staff press the button again.
 */

export function PairDeviceDialog({ device, onClose }: { device: DeviceDto; onClose: () => void }) {
  const [minted, setMinted] = useState<PairingCodeMinted | null>(null);
  const [failed, setFailed] = useState(false);
  const [minting, setMinting] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Ref, not state: the guard must see the CURRENT flight, and `mint` is a
  // stable callback whose closure would otherwise hold a stale boolean. Two
  // server-side mints racing each other is exactly the interleaving the
  // pairing service defends against; the button should not manufacture it.
  const inFlight = useRef(false);

  const mint = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setMinting(true);
    setMinted(null);
    setFailed(false);
    try {
      setMinted(await api.post<PairingCodeMinted>(`/devices/${device.id}/pairing-code`));
    } catch {
      setFailed(true);
    } finally {
      inFlight.current = false;
      setMinting(false);
    }
  }, [device.id]);

  useEffect(() => {
    void mint();
  }, [mint]);

  // A ticking countdown, not a static "expires at 21:15": the person reading
  // this is about to walk across a room, and "9:41 left" answers the question
  // they actually have.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <PairDeviceDialogView
      deviceName={device.name}
      minted={minted}
      failed={failed}
      minting={minting}
      remainingSeconds={minted ? remainingSeconds(minted.expiresAt, now) : null}
      onMintAgain={() => void mint()}
      onClose={onClose}
    />
  );
}

export function remainingSeconds(expiresAt: string, nowMs: number): number {
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - nowMs) / 1000));
}

/** 872 → "14:32". Zero is the caller's cue to show the expired state instead. */
export function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

interface PairDeviceDialogViewProps {
  deviceName: string;
  minted: PairingCodeMinted | null;
  failed: boolean;
  /** A mint request is in flight — the mint button must not fire another. */
  minting?: boolean;
  /** Null while minting; 0 means the code has expired on screen. */
  remainingSeconds: number | null;
  onMintAgain: () => void;
  onClose: () => void;
}

/**
 * Presentational half, split out so the code/countdown rendering is testable
 * without a fetch. The code arrives from the server already grouped
 * (`ABCD-2345`) and is rendered verbatim.
 */
export function PairDeviceDialogView({
  deviceName,
  minted,
  failed,
  minting = false,
  remainingSeconds: remaining,
  onMintAgain,
  onClose,
}: PairDeviceDialogViewProps) {
  const { t } = useTranslation();
  const expired = remaining === 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl">
        <h2 className="mb-1 text-lg font-semibold text-content">
          {t('pairing.title', { device: deviceName })}
        </h2>
        <p className="mb-4 text-sm text-content-secondary">
          {t('pairing.instructions', { device: deviceName })}
        </p>

        {failed ? (
          <p
            role="alert"
            className="rounded-control border border-danger/50 bg-danger/10 p-3 text-sm text-content"
          >
            {t('pairing.failed')}
          </p>
        ) : !minted ? (
          <p className="py-6 text-center text-sm text-content-muted">{t('common.loading')}</p>
        ) : (
          <>
            <div
              className={`rounded-card border border-subtle bg-input py-5 text-center font-mono text-4xl font-bold tracking-[0.15em] text-content ${expired ? 'opacity-30' : ''}`}
            >
              {minted.code}
            </div>

            <p
              className={`tabular mt-3 text-center text-sm ${expired ? 'text-danger' : 'text-content-secondary'}`}
            >
              {expired
                ? t('pairing.expired')
                : t('pairing.expiresIn', { time: formatCountdown(remaining ?? 0) })}
            </p>

            <p className="mt-3 text-xs leading-relaxed text-content-muted">
              {t('pairing.shownOnce')}
            </p>
          </>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onMintAgain}
            // Disabled while a mint is in flight: a double-click would race two
            // server-side mints against each other for the same device.
            disabled={minting}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('pairing.mintAgain')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
          >
            {t('pairing.done')}
          </button>
        </div>
      </div>
    </div>
  );
}
